// Arena v1.0.2 redraws its chart on window resize while retaining its seat store.
// Never redraw during a transition: its detached SVG can still update the new plot.
const QUIET_PERIOD_MS = 200
const RESET_TIMEOUT_MS = 3500

export function attachArenaMapReset(root: ShadowRoot) {
  let timer: number | undefined
  let deadline: number | undefined
  let pending = false
  let disposed = false
  const pointers = new Set<number>()

  function clearTimers() {
    window.clearTimeout(timer)
    window.clearTimeout(deadline)
    timer = undefined
    deadline = undefined
  }

  function cancelReset() {
    clearTimers()
    pending = false
  }

  function scheduleReset() {
    window.clearTimeout(timer)
    if (!pending || pointers.size > 0) return

    timer = window.setTimeout(() => {
      cancelReset()
      if (disposed || !root.host.isConnected || !root.querySelector('svg[class~="map-svg"]')) return
      // Use Arena's own listener to replace both its visual and internal zoom state.
      window.dispatchEvent(new Event('resize'))
    }, QUIET_PERIOD_MS)
  }

  function handleClick(event: Event) {
    const resetButton = event.composedPath().find(target =>
      target instanceof HTMLButtonElement &&
      target.matches('app-navigation button[aria-label="Reset Map"]'),
    )
    if (!resetButton && !pending) return

    event.preventDefault()
    event.stopImmediatePropagation()
    if (!resetButton || pending || !root.querySelector('svg[class~="map-svg"]')) return

    pending = true
    deadline = window.setTimeout(cancelReset, RESET_TIMEOUT_MS)
    scheduleReset()
  }

  function handlePointerDown(event: PointerEvent) {
    if (pending) {
      event.preventDefault()
      event.stopImmediatePropagation()
      return
    }
    pointers.add(event.pointerId)
  }

  function handlePointerEnd(event: PointerEvent) {
    pointers.delete(event.pointerId)
    scheduleReset()
  }

  function handleNavigation(event: Event) {
    if (!pending) return
    event.preventDefault()
    event.stopImmediatePropagation()
  }

  function handleBlur() {
    pointers.clear()
    cancelReset()
  }

  const observer = new MutationObserver(scheduleReset)
  observer.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['transform'] })
  root.addEventListener('click', handleClick, true)
  root.addEventListener('pointerdown', handlePointerDown, true)
  root.addEventListener('wheel', handleNavigation, { capture: true, passive: false })
  root.addEventListener('touchstart', handleNavigation, { capture: true, passive: false })
  root.addEventListener('mousedown', handleNavigation, true)
  root.addEventListener('dblclick', handleNavigation, true)
  window.addEventListener('pointerup', handlePointerEnd, true)
  window.addEventListener('pointercancel', handlePointerEnd, true)
  window.addEventListener('blur', handleBlur)

  return () => {
    disposed = true
    cancelReset()
    observer.disconnect()
    root.removeEventListener('click', handleClick, true)
    root.removeEventListener('pointerdown', handlePointerDown, true)
    root.removeEventListener('wheel', handleNavigation, true)
    root.removeEventListener('touchstart', handleNavigation, true)
    root.removeEventListener('mousedown', handleNavigation, true)
    root.removeEventListener('dblclick', handleNavigation, true)
    window.removeEventListener('pointerup', handlePointerEnd, true)
    window.removeEventListener('pointercancel', handlePointerEnd, true)
    window.removeEventListener('blur', handleBlur)
  }
}
